// =====================================================================
// Scenes menu — data adapter.
// Maps the founder-edited data files (single source of truth) into the
// page shape the scroll menu renders. Pure functions, no DOM, so this
// runs in Node for the pre-build check (scripts/check-scenes-adapter.mjs).
// =====================================================================
import { coffeePages } from '../../data/coffeePages.js';
import { foodPages } from '../../data/foodPages.js';
import { signaturePages } from '../../data/signaturePages.js';

// Page palettes (prototype PAL, verbatim).
// `faint` is a third text level under ink and muted, for the varietal line —
// the name reads first, the flavour note second, the varietal last. Solid
// tones, never opacity, and each clears WCAG AA on its own page (5.2–6.4:1).
export const PAL = {
  coffee: { bg: '#F5F3EB', ink: '#1F140E', muted: '#5C4A3E', faint: '#6E5C4E', accent: '#8E4422', rule: 'rgba(31,20,14,.16)' },
  night: { bg: '#1F140E', ink: '#F5F3EB', muted: '#D4C4A6', faint: '#A8977F', accent: '#e8b86b', rule: 'rgba(245,243,235,.18)' },
  food: { bg: '#EFE7D5', ink: '#1F140E', muted: '#5C4A3E', faint: '#6E5C4E', accent: '#234A3F', rule: 'rgba(31,20,14,.16)' },
  tea: { bg: '#F5F3EB', ink: '#1F140E', muted: '#5C4A3E', faint: '#6E5C4E', accent: '#5A4A3A', rule: 'rgba(31,20,14,.16)' },
};

// data slug → section id (section ids are load-bearing: routing, tabs, scroll-spy)
const SLUG_ID = {
  'espresso/black': 's-esp',
  'espresso/milk': 's-milk',
  'filter/microlots': 's-micro',
  'filter/experimental': 's-exp',
  'filter/estates': 's-est',
  pasta: 's-pasta',
  'fish-brunch': 's-fish',
  rice: 's-rice',
  'burger-pizza': 's-pizza',
  bites: 's-bites',
  dessert: 's-dessert',
  'bakery-smoothies': 's-bakery',
  drinks: 's-tea',
};

const FOLIO = {
  's-esp': '04', 's-milk': '05', 's-micro': '06', 's-exp': '07', 's-est': '08',
  's-pasta': '09', 's-fish': '10', 's-rice': '11', 's-pizza': '12', 's-bites': '13',
  's-dessert': '14', 's-bakery': '15', 's-tea': '16',
};

const RUNNER = {
  's-esp': 'esp', 's-milk': 'esp', 's-micro': 'filter', 's-exp': 'filter', 's-est': 'filter',
  's-pasta': 'kitchen', 's-fish': 'kitchen', 's-rice': 'kitchen', 's-pizza': 'kitchen', 's-bites': 'kitchen',
  's-dessert': 'dessert', 's-bakery': 'bakery', 's-tea': 'tea',
};

const CHAP = {
  's-esp': 'chapCoffee', 's-milk': 'chapCoffee', 's-micro': 'chapCoffee', 's-exp': 'chapCoffee', 's-est': 'chapCoffee',
  's-pasta': 'chapFood', 's-fish': 'chapFood', 's-rice': 'chapFood', 's-pizza': 'chapFood', 's-bites': 'chapFood',
  's-dessert': 'chapFood', 's-bakery': 'chapFood', 's-tea': 'chapTea',
};

const MARK = {
  's-esp': 'Espresso', 's-milk': 'Espresso', 's-micro': 'Filter', 's-exp': 'Filter', 's-est': 'Filter',
  's-pasta': 'Kitchen', 's-fish': 'Kitchen', 's-rice': 'Kitchen', 's-pizza': 'Kitchen', 's-bites': 'Kitchen',
  's-dessert': 'Dessert', 's-bakery': 'Bakery', 's-tea': 'Tea',
};

const live = (lots) => lots.filter((l) => !l.archived);
const photoPath = (p) => (p ? (p.startsWith('/') ? p : '/menu-photos/' + p) : undefined);

export function adaptPage(page) {
  const id = SLUG_ID[page.slug];
  if (!id) return null;
  const cats = page.categories
    .map((cat) => ({
      label: cat.label,
      lede: cat.lede || '',
      ledeId: cat.ledeId || '',
      lots: live(cat.lots).map((l) => ({ ...l, photo: photoPath(l.photo) })),
    }))
    .filter((cat) => cat.lots.length);
  return {
    id,
    slug: page.slug,
    chap: CHAP[id],
    runner: RUNNER[id],
    // One set of headlines for both languages: page names are the menu's
    // own nouns (Black, Milk-based, Fresh pasta), not copy to translate.
    head: { en: { pre: page.headline.prefix || '', it: page.headline.italic } },
    lede: page.lede || '',
    ledeId: page.ledeId || '',
    pull: page.sidebar ? { label: page.sidebar.label.replace(/\n/g, ' '), text: page.sidebar.text, textId: page.sidebar.textId || '' } : undefined,
    modifier: page.modifier || '',
    folio: FOLIO[id],
    tone: id === 's-est' ? 'night' : 'day',
    foot: RUNNER[id] === 'kitchen', // the localised line itself comes from config.ui
    tea: id === 's-tea',
    cats,
  };
}

const menuPages = (pages) => pages.filter((p) => p.kind === 'menu').map(adaptPage).filter(Boolean);

export const coffeeA = () => menuPages(coffeePages.filter((p) => (p.slug || '').startsWith('espresso')));
export const coffeeB = () => menuPages(coffeePages.filter((p) => (p.slug || '').startsWith('filter')));
export const foodList = () => menuPages(foodPages);

const SIG_ID = { '': 's-sig', matcha: 's-matcha', montblanc: 's-mont' };
const SIG_TONE = { '': 'day', matcha: 'night', montblanc: 'day' };
export const signatures = () =>
  signaturePages
    .filter((p) => p.kind === 'signature-drink')
    .map((p) => ({ ...p, id: SIG_ID[p.slug] || 's-sig', pageTone: SIG_TONE[p.slug] || 'day' }));

// Chapter III opener table — 12 plates (prototype TILES).
const TILES = [
  ['pesto-di-casa', 'Pesto di Casa', 's-pasta'],
  ['ragu-bolognese', 'Ragu alla Bolognese', 's-pasta'],
  ['red-curry-salmon', 'Red Curry Salmon', 's-fish'],
  ['wagyu-rendang', 'Wagyu Beef Rendang', 's-rice'],
  ['tuna-toro-bowl', 'Tuna Toro Bowl', 's-rice'],
  ['ba-classic', 'B.A Classic burger', 's-pizza'],
  ['truffle-chicken-pizza', 'Truffle Chicken and Mushroom pizza', 's-pizza'],
  ['margherita-pizza', 'Margherita Classica', 's-pizza'],
  ['pistachio-tiramisu', 'Pistachio Tiramisu', 's-dessert'],
  ['souffle-pancake', 'Soufflé Pancake', 's-dessert'],
  ['basque-cheesecake', 'Basque Cheesecake', 's-dessert'],
  ['blush-bloom', 'Blush Bloom smoothie', 's-bakery'],
];
// Tiles use the small versions (public/menu-photos/thumbs, `npm run thumbs`):
// a three-column grid never needs the 1200px plates.
export const tiles = () => TILES.map(([f, name, sec]) => ({ src: `/menu-photos/thumbs/${f}.jpg`, name, sec }));

// Header tab strip: [id, numeral, section, chapterStart]
export const TABS = [
  ['sig', 'I', 's-sig', 1],
  ['esp', 'II', 's-esp', 1],
  ['filter', '', 's-filter', 0],
  ['kitchen', 'III', 's-pasta', 1],
  ['dessert', '', 's-dessert', 0],
  ['bakery', '', 's-bakery', 0],
  ['tea', 'IV', 's-tea', 1],
];

export const SECTION_TAB = {
  's-sig': 'sig', 's-matcha': 'sig', 's-mont': 'sig',
  's-coffee': 'esp', 's-esp': 'esp', 's-milk': 'esp',
  's-filter': 'filter', 's-micro': 'filter', 's-exp': 'filter', 's-est': 'filter',
  's-food': 'kitchen', 's-pasta': 'kitchen', 's-fish': 'kitchen', 's-rice': 'kitchen', 's-pizza': 'kitchen', 's-bites': 'kitchen',
  's-dessert': 'dessert', 's-bakery': 'bakery', 's-tea': 'tea',
};

// Tappable items of a page (open the detail overlay): anything with a credit, notes or photo.
export function flat(pg) {
  const out = [];
  pg.cats.forEach((cat, ci) =>
    cat.lots.forEach((c, li) => {
      if (c.credit || c.notes || c.photo) out.push({ c, cat, ci, li });
    }),
  );
  return out;
}

// The one plate on a food page: first item with photo && signature, else first with a photo.
export function plateOf(pg) {
  const all = pg.cats.flatMap((cat, ci) => cat.lots.map((c, li) => ({ c, cat, ci, li })));
  return all.find((x) => x.c.photo && x.c.signature) || all.find((x) => x.c.photo) || null;
}
